import { ChevronDown, Tag } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

import { StatusOption } from '../constants/statusOptions'

interface SelectFilterProps {
  title: string
  placeholder?: string
  selectionOptions: StatusOption[]
  activeSelections: string[]
  onSelectionsChange: (statuses: string[]) => void
  searchActive?: boolean
  icon?: React.ReactNode
}

export const SelectFilter = ({
  selectionOptions,
  activeSelections,
  onSelectionsChange,
  title,
  placeholder = 'Buscar estado...',
  searchActive = true,
  icon,
}: SelectFilterProps) => {
  const toggleStatus = (value: string) => {
    if (value === 'all') {
      onSelectionsChange(['all'])
      return
    }

    // Remove "all" if it's in the array
    let newStatuses = activeSelections.filter((s) => s !== 'all')

    // Toggle the selected status
    if (newStatuses.includes(value)) {
      newStatuses = newStatuses.filter((s) => s !== value)
      // If no statuses left, set back to "all"
      if (newStatuses.length === 0) {
        newStatuses = ['all']
      }
    } else {
      newStatuses.push(value)
    }

    onSelectionsChange(newStatuses)
  }

  const getActiveCount = () => {
    if (activeSelections.includes('all')) return 0
    return activeSelections.length
  }

  const activeCount = getActiveCount()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-1.5" aria-label={title}>
          {icon ? icon : <Tag className="h-4 w-4" />}
          <span>{title}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
          {activeCount > 0 && (
            <Badge variant="secondary" className="ml-1 rounded-full px-1.5">
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          {searchActive && <CommandInput placeholder={placeholder} />}
          <CommandList>
            <CommandEmpty>No se encontraron resultados.</CommandEmpty>
            <CommandGroup>
              {selectionOptions.map((status) => (
                <CommandItem
                  key={status.value}
                  onSelect={() => toggleStatus(status.value)}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <div className="flex flex-1 items-center gap-2">
                    <Checkbox
                      checked={activeSelections.includes(status.value)}
                      onCheckedChange={() => toggleStatus(status.value)}
                    />
                    <span>{status.label}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
